import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MenuBar from "@/components/MenuBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar, Users, MessageSquare, Edit, Archive, Trash2, LayoutDashboard, ChevronDown, Eye, FileEdit } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AMAService } from "@/services/amaService";
import { AMA } from "@/types/ama";
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import { toast } from "sonner";

const AMAManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [selectedAMA, setSelectedAMA] = useState<AMA | null>(null);

  const { data: amas, isLoading } = useQuery({
    queryKey: ['amas'],
    queryFn: () => AMAService.getAllAMAs(),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => AMAService.archiveAMA(id),
    onSuccess: () => {
      toast.success(t('ama.management.archived'));
      queryClient.invalidateQueries({ queryKey: ['amas'] });
      queryClient.invalidateQueries({ queryKey: ['current-ama'] });
      setArchiveDialogOpen(false);
    },
    onError: () => {
      toast.error(t('ama.management.archiveError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => AMAService.deleteAMA(id),
    onSuccess: () => {
      toast.success(t('ama.management.deleted'));
      queryClient.invalidateQueries({ queryKey: ['amas'] });
      setDeleteDialogOpen(false);
    },
    onError: () => {
      toast.error(t('ama.management.deleteError'));
    },
  });

  const handleArchive = (ama: AMA, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAMA(ama);
    setArchiveDialogOpen(true);
  };

  const handleDelete = (ama: AMA, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAMA(ama);
    setDeleteDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      planned: "secondary",
      live: "destructive",
      archived: "outline",
    } as const;

    const labels = {
      planned: t('ama.status.planned'),
      live: t('ama.status.live'),
      archived: t('ama.status.archived'),
    };

    return (
      <Badge variant={variants[status as keyof typeof variants]}>
        {labels[status as keyof typeof labels]}
      </Badge>
    );
  };

  if (isLoading) {
    return <div className="p-6">{t('common.loading')}</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <MenuBar />
      
      <div className="container mx-auto p-6 space-y-6">
        {/* AMA Navigation Dropdown Menu */}
        <div className="flex items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <LayoutDashboard className="h-4 w-4" />
                AMA Admin
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-64 bg-background z-50">
              <DropdownMenuLabel>Admin/Redaksjon</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/editorial/ama')}>
                <LayoutDashboard className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Oversikt</span>
                  <span className="text-xs text-muted-foreground">Alle AMAs</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/editorial/ama/new')}>
                <Plus className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Opprett ny AMA</span>
                  <span className="text-xs text-muted-foreground">Lag ny AMA-sesjon</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <FileEdit className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Rediger AMA</span>
                  <span className="text-xs text-muted-foreground">/editorial/ama/:id/edit</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <MessageSquare className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Modereringspanel</span>
                  <span className="text-xs text-muted-foreground">/editorial/ama/:id</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Offentlige sider</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => navigate('/ama')}>
                <Eye className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>AMA-oversikt</span>
                  <span className="text-xs text-muted-foreground">Aktiv/arkiverte AMAs</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Eye className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Spesifikk AMA</span>
                  <span className="text-xs text-muted-foreground">/ama/:id</span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <Edit className="mr-2 h-4 w-4" />
                <div className="flex flex-col">
                  <span>Ekspert svar-side</span>
                  <span className="text-xs text-muted-foreground">/ama/answer/:token</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button onClick={() => navigate('/editorial/ama/new')} className="gap-2">
            <Plus className="h-4 w-4" />
            Opprett ny AMA
          </Button>
        </div>

      <div>
        <h1 className="text-3xl font-bold">{t('ama.management.title')}</h1>
        <p className="text-muted-foreground mt-2">
          {t('ama.management.description')}
        </p>
      </div>

      <div className="grid gap-4">
        {amas?.map((ama: AMA) => (
          <Card key={ama.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1 flex-1">
                  <CardTitle className="flex items-center gap-2">
                    {ama.topic}
                    {getStatusBadge(ama.status)}
                    {ama.is_featured && (
                      <Badge variant="default">
                        {t('ama.management.featured')}
                      </Badge>
                    )}
                  </CardTitle>
                  <div className="text-sm text-muted-foreground">
                    <Users className="inline h-4 w-4 mr-1" />
                    {ama.expert_name}
                    {ama.expert_company && ` • ${ama.expert_company}`}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/editorial/ama/${ama.id}`)}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/editorial/ama/${ama.id}/edit`)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  {ama.status !== 'archived' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => handleArchive(ama, e)}
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => handleDelete(ama, e)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent
              className="cursor-pointer"
              onClick={() => navigate(`/editorial/ama/${ama.id}`)}
            >
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {formatDistanceToNow(new Date(ama.scheduled_at), {
                    addSuffix: true,
                    locale: nb,
                  })}
                </div>
              </div>
              {ama.description && (
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                  {ama.description}
                </p>
              )}
            </CardContent>
          </Card>
        ))}

        {(!amas || amas.length === 0) && (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {t('ama.management.noAMAs')}
              </p>
              <Button 
                onClick={() => navigate('/editorial/ama/new')}
                className="mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('ama.management.createFirst')}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ama.management.archiveConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ama.management.archiveConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedAMA && archiveMutation.mutate(selectedAMA.id)}
            >
              {t('ama.management.archiveButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ama.management.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ama.management.deleteConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedAMA && deleteMutation.mutate(selectedAMA.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('ama.management.deleteButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default AMAManagement;
